import React, { useState, useEffect } from 'react';
import { getOperationLogs, getLogStatistics, cleanupLogs, exportLogsCSV } from '../api';
import Pagination from '../components/Pagination';

function Logs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [filter, setFilter] = useState({ userId: '', action: '', startDate: '', endDate: '' });

  const actionTypes = ['', 'login', 'create', 'update', 'delete'];

  useEffect(() => { fetchLogs(); fetchStats(); }, [pagination.page, pagination.pageSize]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, pageSize: pagination.pageSize, ...filter };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await getOperationLogs(params);
      if (res.data.success) {
        setLogs(res.data.data);
        if (res.data.pagination) {
          setPagination(prev => ({ ...prev, ...res.data.pagination }));
        }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await getLogStatistics();
      if (res.data.success) setStats(res.data.data);
    } catch (e) { console.error(e); }
  };

  const handleCleanup = async () => {
    const days = window.prompt('请输入保留最近多少天的日志（数字）：', '30');
    if (!days || isNaN(parseInt(days))) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`将删除 ${days} 天前的所有日志，确定继续？`)) return;
    try {
      await cleanupLogs(parseInt(days));
      alert('清理完成');
      fetchLogs();
    } catch (e) { console.error(e); }
  };

  const handleExport = async () => {
    try {
      const res = await exportLogsCSV(filter);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `操作日志_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
  };

  const handleFilter = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const resetFilter = () => {
    setFilter({ userId: '', action: '', startDate: '', endDate: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  return (
    <>
      {/* 统计 */}
      <div className="stats-cards">
        <div className="stats-row">
          <div className="stat-box">
            <div className="stat-label">总操作次数</div>
            <div className="stat-value">{stats.actionStats ? Object.values(stats.actionStats).reduce((a, b) => a + b, 0) : 0}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">登录次数</div>
            <div className="stat-value blue">{stats.actionStats?.login || 0}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">创建操作</div>
            <div className="stat-value green">{stats.actionStats?.create || 0}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">更新操作</div>
            <div className="stat-value orange">{stats.actionStats?.update || 0}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">删除操作</div>
            <div className="stat-value red">{stats.actionStats?.delete || 0}</div>
          </div>
        </div>
      </div>

      {/*趋势图*/}
      {stats.trend && stats.trend.length > 0 && (
        <div className="content-card" style={{ marginTop: 16 }}>
          <div className="section-title">最近7天操作趋势</div>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-end', height: 150, padding: '20px 0' }}>
            {stats.trend.map((t, i) => {
              const maxCount = Math.max(...stats.trend.map(x => x.count), 1);
              const height = Math.max((t.count / maxCount) * 100, 5);
              return (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontWeight: 'bold', fontSize: 14, color: '#1890ff' }}>{t.count}</div>
                  <div style={{
                    width: 40,
                    height: height,
                    backgroundColor: '#1890ff',
                    borderRadius: 4,
                    margin: '4px 0'
                  }}></div>
                  <div style={{ fontSize: 12, color: '#666' }}>{t.date?.substring(5)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 日志列表 */}
      <div className="content-card">
        <div className="section-title">操作日志</div>

        <div className="toolbar-row">
          <div className="toolbar-left">
            <input className="filter-input" type="text" placeholder="用户ID"
              value={filter.userId} onChange={e => setFilter({ ...filter, userId: e.target.value })} />
            <select className="filter-select" value={filter.action} onChange={e => setFilter({ ...filter, action: e.target.value })}>
              {actionTypes.map(a => <option key={a} value={a}>{a || '全部操作'}</option>)}
            </select>
            <input className="filter-input" type="date" value={filter.startDate}
              onChange={e => setFilter({ ...filter, startDate: e.target.value })} />
            <span style={{ color: '#999' }}>-</span>
            <input className="filter-input" type="date" value={filter.endDate}
              onChange={e => setFilter({ ...filter, endDate: e.target.value })} />
            <button className="action-btn btn-blue" onClick={handleFilter}>筛选</button>
            <button className="action-btn btn-gray" onClick={resetFilter}>重置</button>
          </div>
          <div>
            <button className="action-btn btn-orange" onClick={handleCleanup}>清理日志</button>
            <button className="action-btn btn-green" onClick={handleExport}>导出CSV</button>
          </div>
        </div>

        {loading ? <div className="loading-text">加载中...</div> : (
          <>
            <table className="basic-table">
              <thead>
                <tr><th className="no-cell">序号</th><th>用户</th><th>操作</th><th>对象类型</th><th>对象ID</th><th>详情</th><th>IP</th><th>时间</th></tr>
              </thead>
              <tbody>
                {logs.length > 0 ? logs.map((log, i) => (
                  <tr key={log.id}>
                    <td className="no-cell">{(pagination.page - 1) * pagination.pageSize + i + 1}</td>
                    <td>{log.username}</td>
                    <td><span className={`status-badge ${log.action === 'login' ? 'badge-green' : log.action === 'delete' ? 'badge-red' : 'badge-blue'}`}>{log.action}</span></td>
                    <td>{log.target_type}</td>
                    <td>{log.target_id}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.details}>{log.details || '-'}</td>
                    <td>{log.ip_address || '-'}</td>
                    <td>{log.created_at ? log.created_at.replace('T', ' ').substring(0, 16) : '-'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="8" style={{ textAlign: 'center', color: '#999' }}>暂无数据</td></tr>
                )}
              </tbody>
            </table>

            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </>
  );
}

export default Logs;